import { PacWidget } from '@bariendo/lib/patient-access-script';
import { Button, Group, Stack, Text, TextInput, UnstyledButton } from '@mantine/core';
import { useState } from 'react';
import { ORBIT_NEEDS_ACTION, ORBIT_SETTLED } from '@/theme/colors';
import { Row } from './PatientAccessCallWidgets';
import { InsuranceCard } from './useInsuranceCard';

type InsuranceCardFieldWidget = Extract<PacWidget, { kind: 'insuranceCardField' }>;

interface InsuranceCardFieldRowProps {
  widget: InsuranceCardFieldWidget;
  card: InsuranceCard;
  /** False for a role that may not edit the insurance card. */
  editable: boolean;
}

type SaveState = 'idle' | 'saving' | 'saved' | 'failed';

const NOT_ON_FILE = 'Not on file';

/**
 * A member ID or group number, read from the patient's insurance Coverage and
 * edited in place. An edit saves to the Coverage, never the note, and says so.
 * With no insurance Coverage yet, the value is held until the switch to the
 * Insurance track writes it, and the row says that it is not saved yet.
 */
export function InsuranceCardFieldRow({ widget, card, editable }: InsuranceCardFieldRowProps) {
  const [state, setState] = useState<SaveState>('idle');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const held = card.pending[widget.field];
  const shown = held ?? card.saved[widget.field];
  const canEdit = editable && !card.writing && (card.status === 'found' || card.status === 'none');

  // A role whose policy cannot read Coverage gets no row: there is nothing it could check or correct.
  if (card.status === 'forbidden') return null;

  async function submit(): Promise<void> {
    // Nothing changed: no bot call, no new Coverage version or Provenance. A
    // held value re-saved once the Coverage exists is a change.
    if (draft.trim() === (shown ?? '') && (held === undefined || card.status === 'none')) {
      setEditing(false);
      return;
    }
    setState('saving');
    try {
      const outcome = await card.save(widget.field, draft);
      // A held value is flagged below for as long as it is held.
      setState(outcome === 'saved' ? 'saved' : 'idle');
      setEditing(false);
    } catch {
      setState('failed');
    }
  }

  function cancel(): void {
    setState('idle');
    setEditing(false);
  }

  function startEditing(): void {
    setDraft(shown ?? '');
    setState('idle');
    setEditing(true);
  }

  let control;
  if (editing) {
    control = (
      <Group gap={4} wrap="nowrap" align="flex-start">
        <TextInput
          size="xs"
          aria-label={widget.label}
          value={draft}
          disabled={state === 'saving' || card.writing}
          onChange={(event) => setDraft(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !card.writing) void submit();
            if (event.key === 'Escape') cancel();
          }}
        />
        <Button
          size="xs"
          color="green.9"
          loading={state === 'saving'}
          disabled={card.writing}
          onClick={() => void submit()}
        >
          Save
        </Button>
        <Button size="xs" variant="default" disabled={state === 'saving'} onClick={cancel}>
          Cancel
        </Button>
      </Group>
    );
  } else if (card.status === 'loading' || card.status === 'idle') {
    control = (
      <Text size="sm" c="dimmed" ta="right">
        Loading…
      </Text>
    );
  } else if (card.status === 'unreadable') {
    control = (
      <Text size="sm" c="dimmed" ta="right">
        Couldn&rsquo;t read the insurance card
      </Text>
    );
  } else if (canEdit) {
    control = (
      <UnstyledButton onClick={startEditing} aria-label={`Edit ${widget.label.toLowerCase()}`}>
        <Text size="sm" fw={shown ? 500 : 400} c={shown ? undefined : 'dimmed'} td="underline dotted">
          {shown ?? NOT_ON_FILE}
        </Text>
      </UnstyledButton>
    );
  } else {
    control = (
      <Text size="sm" fw={shown ? 500 : 400} c={shown ? undefined : 'dimmed'} ta="right">
        {shown ?? NOT_ON_FILE}
      </Text>
    );
  }

  return (
    <Row label={widget.label} say={widget.say}>
      <Stack gap={2} align="flex-end">
        {control}
        {held !== undefined && !editing && (
          <Text size="xs" c={ORBIT_NEEDS_ACTION} ta="right" data-testid="insurance-card-held">
            {card.status === 'found'
              ? 'Not saved yet: save it again to put it on the insurance card.'
              : 'Not saved yet: there is no insurance card on file. Moving them to the Insurance track saves it.'}
          </Text>
        )}
        {state === 'saved' && held === undefined && (
          <Text size="xs" c={ORBIT_SETTLED}>
            Saved to insurance card
          </Text>
        )}
        {state === 'failed' && (
          <Text size="xs" c="red.7" role="alert">
            Couldn&rsquo;t save to insurance card
          </Text>
        )}
      </Stack>
    </Row>
  );
}
